import React, { useEffect, useMemo, useState } from 'react';
import { Compass, LayoutGrid, List, RotateCcw } from 'lucide-react';
import type { AppModule, HomePageConfig } from '../../types';
import { useAuth } from '../../auth/AuthContext';
import {
  DEFAULT_HEADER_COLOR,
  DEFAULT_HOME_CONFIG,
  DEFAULT_SUBTITLE_COLOR,
  DEFAULT_TITLE_COLOR,
  settingsService,
} from '../../services/settingsService';
import { moduleService } from '../../services/moduleService';
import { isDashboard } from '../../dashboard/services/dashboardService';
import { sortByHomeOrder } from '../../utils/moduleOrder';
import ImagePicker from './ImagePicker';

const ACCENT_PRESETS = ['#2DD4BF', '#38BDF8', '#818CF8', '#FBBF24', '#F472B6'];

interface Props {
  config: HomePageConfig;
  onSaved: (config: HomePageConfig) => void;
  /** Every app and dashboard this admin can see — the same list
   *  `ContentSpaceTab`'s Organization view already gets from SettingsPage,
   *  reused here for the "which show on this page" checklist below. */
  modules: AppModule[];
  onModulesChanged: (modules: AppModule[]) => void;
}

/** Admin-only. Rendering is guarded by SettingsPage via `can('home:edit')`. */
export default function HomePageEditorTab({ config, onSaved, modules, onModulesChanged }: Props) {
  const { currentUser } = useAuth();
  const [draft, setDraft] = useState<HomePageConfig>(config);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [visibilityError, setVisibilityError] = useState<string | null>(null);

  // Same order the home page itself renders in, so this list reads top to
  // bottom exactly the way the cards do.
  const orderedModules = useMemo(() => sortByHomeOrder(modules), [modules]);

  const toggleShowOnHome = async (module: AppModule) => {
    setTogglingId(module.id);
    setVisibilityError(null);
    try {
      const next = await moduleService.setGalleryFields(currentUser, module.id, {
        showOnHome: module.showOnHome === false,
      });
      onModulesChanged(next);
    } catch (failure) {
      setVisibilityError(
        failure instanceof Error ? failure.message : `Could not update "${module.title || module.name}".`,
      );
    } finally {
      setTogglingId(null);
    }
  };

  useEffect(() => setDraft(config), [config]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(config);

  const update = <K extends keyof HomePageConfig>(key: K, value: HomePageConfig[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSavedAt(null);
  };

  const save = async () => {
    // `title` is edited under General now, but it still rides along in this
    // same draft — trimmed here too, so a save from THIS tab can never
    // persist whitespace the General tab did not put there.
    setSaving(true);
    setError(null);
    try {
      const next = await settingsService.updateHomeConfig({
        ...draft,
        title: draft.title.trim() || draft.title,
      });
      onSaved(next);
      setSavedAt(Date.now());
    } catch (failure) {
      // settingsService has already turned an API failure into a line that
      // says what to do about it — expired session, not an admin, image too
      // large. Shown as-is; the generic fallback would be advice that cannot
      // work for any of those.
      setError(
        failure instanceof Error && failure.message
          ? failure.message
          : 'Could not save changes. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const next = await settingsService.resetHomeConfig();
      setDraft(next);
      onSaved(next);
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const field = (
    label: string,
    key: keyof HomePageConfig,
    options?: { multiline?: boolean; hint?: string },
  ) => (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      {options?.multiline ? (
        <textarea
          value={String(draft[key] ?? '')}
          onChange={(event) => update(key, event.target.value as HomePageConfig[typeof key])}
          rows={2}
          className="mt-1.5 w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
        />
      ) : (
        <input
          value={String(draft[key] ?? '')}
          onChange={(event) => update(key, event.target.value as HomePageConfig[typeof key])}
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
        />
      )}
      {options?.hint && <span className="mt-1 block text-[11px] text-slate-400">{options.hint}</span>}
    </label>
  );

  /**
   * Colour row for an optional text colour. `fallback` is what the page uses
   * when the value is unset, so the swatch always shows the real rendered
   * colour rather than an empty control.
   */
  const textColorRow = (
    label: string,
    key: 'eyebrowColor' | 'titleColor' | 'subtitleColor' | 'headerColor',
    fallback: string,
    fallbackLabel: string,
  ) => {
    const current = draft[key];
    // <input type="color"> only accepts hex, so an rgba() fallback needs one.
    const swatchValue = current && /^#[0-9a-f]{6}$/i.test(current) ? current : '#CBE1FA';

    return (
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-slate-600">{label}</span>
        <div className="flex items-center gap-2">
          {!current && <span className="text-[10px] text-slate-400">{fallbackLabel}</span>}
          {current && (
            <button
              type="button"
              onClick={() => update(key, undefined)}
              className="text-[10px] font-medium text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline"
            >
              Reset
            </button>
          )}
          <span
            aria-hidden="true"
            className="h-5 w-5 rounded border border-slate-200"
            style={{ backgroundColor: current || fallback }}
          />
          <input
            type="color"
            value={swatchValue}
            onChange={(event) => update(key, event.target.value)}
            aria-label={label}
            className="h-7 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* ---- Live preview ---- */}
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Live preview
        </p>
        <div
          className="rounded-xl px-5 py-5 text-center"
          style={{
            background: 'linear-gradient(180deg, #0C2947 0%, #103A63 100%)',
          }}
        >
          {/* Brand mark is HARDCODED — intentionally not editable. */}
          <div className="mb-3 flex justify-center">
            <div className="inline-flex overflow-hidden rounded-lg border-2 border-white/20">
              <div className="flex items-center gap-1.5 bg-[#0F2942] px-2.5 py-1">
                <Compass className="h-3 w-3 text-teal-400" />
                <span className="text-xs font-bold text-white">Blue</span>
              </div>
              <div className="bg-white px-2.5 py-1">
                <span className="text-xs font-bold text-[#0F2942]">View</span>
              </div>
            </div>
          </div>
          <p
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: draft.eyebrowColor || draft.accentColor }}
          >
            {draft.eyebrow || ' '}
          </p>
          <h3
            className="mt-1.5 text-xl font-semibold"
            style={{ color: draft.titleColor || DEFAULT_TITLE_COLOR }}
          >
            {draft.title || 'Untitled'}
          </h3>
          <p
            className="mx-auto mt-1.5 max-w-xs text-[11px] leading-relaxed"
            style={{ color: draft.subtitleColor || DEFAULT_SUBTITLE_COLOR }}
          >
            {draft.subtitle}
          </p>
          <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-200/70">
              {draft.modulesSectionLabel}
            </span>
            <span
              className="rounded-md px-2.5 py-1 text-[10px] font-semibold text-[#0B1F38]"
              style={{ backgroundColor: draft.accentColor }}
            >
              + {draft.addModuleButtonLabel}
            </span>
          </div>
        </div>
      </div>

      {/* ---- Editable fields ---- */}
      <div className="space-y-3">
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
          The portal's name and logo moved to <strong>General</strong>. Everything below is the hero
          itself — its wording, colours and background.
        </p>
        {field('Eyebrow / kicker', 'eyebrow')}
        {field('Subtitle', 'subtitle', { multiline: true })}
        {field('Modules section label', 'modulesSectionLabel')}
        {field('Add Module button label', 'addModuleButtonLabel')}

        <div>
          <span className="text-xs font-semibold text-slate-600">Accent colour</span>
          <div className="mt-1.5 flex items-center gap-2">
            {ACCENT_PRESETS.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => update('accentColor', color)}
                aria-label={`Accent ${color}`}
                className={`h-7 w-7 rounded-full border-2 transition ${
                  draft.accentColor?.toLowerCase() === color.toLowerCase()
                    ? 'border-slate-800 scale-110'
                    : 'border-transparent'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
            <input
              type="color"
              value={draft.accentColor || '#2DD4BF'}
              onChange={(event) => update('accentColor', event.target.value)}
              aria-label="Custom accent colour"
              className="h-7 w-10 cursor-pointer rounded border border-slate-200 bg-white"
            />
          </div>
        </div>

        {/* Text colours. Each falls back to a default when cleared, so an admin
            can always get back to the stock look. */}
        {textColorRow(
          'Eyebrow / kicker colour',
          'eyebrowColor',
          draft.accentColor || DEFAULT_HOME_CONFIG.accentColor,
          'Matches accent',
        )}
        {textColorRow('Application title colour', 'titleColor', DEFAULT_TITLE_COLOR, 'Default')}
        {textColorRow('Subtitle colour', 'subtitleColor', DEFAULT_SUBTITLE_COLOR, 'Default')}
        {textColorRow('Header background colour', 'headerColor', DEFAULT_HEADER_COLOR, 'Default')}

        <ImagePicker
          label="Background image"
          value={draft.backgroundImageUrl}
          onChange={(url) => update('backgroundImageUrl', url)}
          previewAspect="wide"
          /* Full-bleed, so it gets a wider gate and more pixels. It still has
             to share a ~5 MB localStorage quota with every module and user
             record, which is why the stored copy is squeezed to ~600 KB — the
             picture behind a scrim does not need more. */
          maxBytes={5 * 1024 * 1024}
          maxDimension={2200}
          targetBytes={600 * 1024}
          hint="Up to 5 MB — large photos are resized automatically. Sits behind the home page; the gradient is used when empty."
        />

        {draft.backgroundImageUrl && (
          <label className="block">
            <span className="flex items-center justify-between text-xs font-semibold text-slate-600">
              Background dimming
              <span className="font-normal text-slate-400">
                {Math.round((draft.backgroundOverlay ?? 0.8) * 100)}%
              </span>
            </span>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round((draft.backgroundOverlay ?? 0.8) * 100)}
              onChange={(event) => update('backgroundOverlay', Number(event.target.value) / 100)}
              className="mt-1.5 w-full accent-teal-500"
            />
            <span className="mt-1 block text-[11px] text-slate-400">
              Darkens the image so the title stays readable.
            </span>
          </label>
        )}
      </div>

      {/* ---- Which apps/dashboards show on the home page ----
          Deliberately saves each row the instant it's toggled, the same
          moduleService.setGalleryFields call AppGalleryTab's own "Show on
          Home Page"/"Hide from Home Page" menu item already uses — this is
          the same flag, just gathered into one checklist instead of having
          to open each app's own 3-dot menu one at a time. Separate from the
          hero's Save/Cancel draft above: there is nothing to lose by
          Cancelling the hero text after ticking a box here. Only an admin
          ever reaches this tab at all (see this file's own top doc comment),
          so this list is already the only place anyone adds or removes a
          card from the home page — the gallery's own per-app toggle is
          exactly as admin-only as this is. */}
      <div>
        <div className="mb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Apps &amp; dashboards on this page
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500">
            Checked items show as a card on the home page; unchecked ones stay reachable from My
            Space / Organization only.
          </p>
        </div>

        {visibilityError && (
          <p role="alert" className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
            {visibilityError}
          </p>
        )}

        {orderedModules.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 px-3 py-6 text-center text-xs text-slate-400">
            No applications or dashboards yet.
          </p>
        ) : (
          <div className="max-h-72 space-y-0.5 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-1.5">
            {orderedModules.map((module) => {
              const checked = module.showOnHome !== false;
              const busy = togglingId === module.id;
              return (
                <label
                  key={module.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1.5 hover:bg-white"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy}
                    onChange={() => void toggleShowOnHome(module)}
                    className="h-3.5 w-3.5 flex-shrink-0 accent-teal-600"
                  />
                  {isDashboard(module) ? (
                    <List className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                  ) : (
                    <LayoutGrid className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-[12.5px] text-slate-700">
                    {module.title || module.name}
                  </span>
                  <span className="flex-shrink-0 text-[9.5px] uppercase tracking-wide text-slate-400">
                    {isDashboard(module) ? 'Dashboard' : 'App'}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* ---- Locked items, shown so the rule is explicit to the Admin ---- */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Fixed brand elements
        </p>
        <ul className="mt-1.5 space-y-1 text-[11px] text-slate-500">
          <li>• The “Blue / View” brand mark is fixed and cannot be edited.</li>
          <li>• The “powered by MapGeoid” footer is fixed and cannot be removed.</li>
        </ul>
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {error}
        </p>
      )}
      {savedAt && !isDirty && (
        <p className="rounded-lg bg-teal-50 px-3 py-2 text-xs font-medium text-teal-700">
          Home page updated.
        </p>
      )}

      <div className="flex items-center gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={save}
          disabled={!isDirty || saving}
          className="rounded-lg bg-[#0F2942] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={() => setDraft(config)}
          disabled={!isDirty || saving}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={saving || JSON.stringify(config) === JSON.stringify(DEFAULT_HOME_CONFIG)}
          className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition hover:text-slate-800 disabled:opacity-40"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
